/* ============================================================================
   <unc-menu>  the mobile navigation sheet

   Replaces the stock Dawn <header-drawer>, which was a <details>/<summary>
   with three levels of submenu machinery. The Uncaffed menu is five flat rows,
   so none of that was doing anything: see the note in sections/header.liquid.

   THE TRIGGER LIVES OUTSIDE THIS ELEMENT, in the header's icon row, because
   that is where the comp puts it and where the header's own grid can place it.
   They are joined by aria-controls rather than by DOM nesting, which is also
   the accessible wiring: the button says what it opens.

   THE SHEET IS RENDERED AFTER </sticky-header>, NOT INSIDE IT. It is
   position:fixed, and a fixed element takes its containing block from the
   nearest transformed ancestor rather than the viewport. assets/header-position.js
   animates the sticky header, so a sheet nested inside it would pin itself to a
   moving header instead of to the screen.
   ============================================================================ */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  if (!customElements.get('unc-menu')) {
    customElements.define(
      'unc-menu',
      class UncMenu extends HTMLElement {
        connectedCallback() {
          /* REPARENT TO <body> BEFORE ANYTHING ELSE.

             This element is position:fixed, and it is rendered inside the header
             section. That section is position:sticky and the theme gives it a
             will-change, which makes it a containing block for fixed
             descendants: `top` and `bottom` then resolve against the 114px-tall
             header instead of the viewport, and the sheet computes to a height
             of exactly 0. It opened, the hamburger turned into an X, and
             nothing appeared, because there was nothing to see.

             Moving it re-fires this callback, so the move returns immediately
             and the second pass does the wiring. */
          if (this.parentElement !== document.body) {
            document.body.appendChild(this);
            return;
          }

          this.trigger = document.querySelector('[aria-controls="' + this.id + '"]');
          if (!this.trigger) return;

          this.backdrop = this.querySelector('[data-unc-menu-backdrop]');

          this.onTrigger = this.onTrigger.bind(this);
          this.onKeydown = this.onKeydown.bind(this);
          this.onClick = this.onClick.bind(this);
          this.onResize = this.onResize.bind(this);

          this.trigger.addEventListener('click', this.onTrigger);
          this.addEventListener('click', this.onClick);
          document.addEventListener('keydown', this.onKeydown);
          window.addEventListener('resize', this.onResize);

          this.close(true);
        }

        disconnectedCallback() {
          if (this.trigger) this.trigger.removeEventListener('click', this.onTrigger);
          document.removeEventListener('keydown', this.onKeydown);
          window.removeEventListener('resize', this.onResize);
          document.body.classList.remove('overflow-hidden');
        }

        get isOpen() {
          return this.trigger.getAttribute('aria-expanded') === 'true';
        }

        onTrigger(e) {
          e.preventDefault();
          if (this.isOpen) this.close();
          else this.open();
        }

        onClick(e) {
          if (this.backdrop && e.target === this.backdrop) {
            this.close();
            return;
          }
          /* A link to the page you are already on fires no navigation, so the
             sheet would stay open over the destination. Closing on any link
             covers that and costs nothing on a real navigation. */
          if (e.target.closest('a[href]')) this.close();
        }

        onKeydown(e) {
          if (!this.isOpen) return;
          if (e.key === 'Escape') {
            e.preventDefault();
            this.close();
            this.trigger.focus();
            return;
          }
          if (e.key !== 'Tab') return;

          var items = Array.prototype.filter.call(
            this.querySelectorAll(FOCUSABLE),
            function (el) {
              return el.offsetParent !== null;
            }
          );
          if (!items.length) return;
          var first = items[0];
          var last = items[items.length - 1];

          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }

        /* The sheet is mobile-only and the desktop nav takes over at 990px. A
           menu left open across that boundary is invisible but still holds the
           scroll lock, so the page silently stops scrolling. */
        onResize() {
          if (this.isOpen && window.innerWidth >= 990) this.close();
        }

        /* MEASURED AT OPEN TIME, not computed from CSS custom properties.

           header-position.js maintains --header-height and
           --announcement-bar-height, and adding them is right only at the top
           of the page. The announcement bar scrolls away and the header pins
           itself to the top, so anywhere below the fold that sum overshoots by
           the height of the bar and the sheet opens with a strip of the page
           showing above it. The header's own bottom edge is true at any scroll
           position. */
        setTop() {
          var bar = this.trigger.closest('.header-wrapper') || this.trigger.closest('header');
          if (!bar) return;
          var bottom = Math.max(0, Math.round(bar.getBoundingClientRect().bottom));
          this.style.setProperty('--unc-mh-top', bottom + 'px');
        }

        open() {
          this.setTop();
          this.trigger.setAttribute('aria-expanded', 'true');
          this.hidden = false;
          document.body.classList.add('overflow-hidden');

          var first = this.querySelector(FOCUSABLE);
          if (first) first.focus();
        }

        /* `quiet` is the initial call from connectedCallback: set the closed
           state without touching the body class, which the cart drawer may
           legitimately be holding. */
        close(quiet) {
          this.trigger.setAttribute('aria-expanded', 'false');
          this.hidden = true;
          if (!quiet) document.body.classList.remove('overflow-hidden');
        }
      }
    );
  }
})();
